'use client';

import { useEffect, useRef, useState } from 'react';

export default function BrowserJpgConverter() {
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [fileName, setFileName] = useState('image');
  const [quality, setQuality] = useState(0.9);
  const [background, setBackground] = useState('#ffffff');
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const downloadUrlRef = useRef<string | null>(null);

  useEffect(() => {
    downloadUrlRef.current = downloadUrl;
  }, [downloadUrl]);

  useEffect(() => {
    return () => {
      if (downloadUrlRef.current) {
        URL.revokeObjectURL(downloadUrlRef.current);
      }
    };
  }, []);

  function handleFile(file: File | undefined) {
    if (!file) {
      return;
    }
    setError(null);
    setFileName(file.name.replace(/\.[^.]+$/, '') || 'image');
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      setImage(img);
      URL.revokeObjectURL(url);
    };
    img.onerror = () => {
      setError('This image could not be read. Try a PNG, JPG, WebP, GIF, or BMP file.');
      URL.revokeObjectURL(url);
    };
    img.src = url;
  }

  useEffect(() => {
    if (!image || !canvasRef.current) {
      return;
    }
    if (typeof document === 'undefined') {
      return;
    }
    const canvas = canvasRef.current;
    canvas.width = image.naturalWidth;
    canvas.height = image.naturalHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return;
    }
    // JPG has no alpha channel, so flatten transparency onto the chosen color.
    ctx.fillStyle = background;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(image, 0, 0);
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          setError('The conversion could not finish for this file.');
          return;
        }
        setDownloadUrl((prev) => {
          if (prev) {
            URL.revokeObjectURL(prev);
          }
          return URL.createObjectURL(blob);
        });
      },
      'image/jpeg',
      quality,
    );
  }, [image, quality, background]);

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">JPG</span>
        <h2 className="tool-live-title">Convert an image to JPG</h2>
        <p className="tool-live-subtitle">
          Upload any PNG, JPG, WebP, GIF, or BMP image and download a JPG with your own quality and
          background color, entirely in your browser. Nothing is uploaded to a server.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <label className="tool-live-field tool-live-field-full">
            <span>Choose an image</span>
            <input
              accept="image/*"
              onChange={(event) => handleFile(event.target.files?.[0])}
              type="file"
            />
          </label>

          <label className="tool-live-field tool-live-field-full">
            <span>Quality: {Math.round(quality * 100)}%</span>
            <input
              max={1}
              min={0.1}
              onChange={(event) => setQuality(Number(event.target.value))}
              step={0.05}
              type="range"
              value={quality}
            />
          </label>

          <label className="tool-live-field">
            <span>Background fill</span>
            <input
              onChange={(event) => setBackground(event.target.value)}
              type="color"
              value={background}
            />
          </label>

          <div className="tool-live-actions">
            {downloadUrl ? (
              <a
                className="button button-secondary"
                download={`${fileName}.jpg`}
                href={downloadUrl}
              >
                Download JPG
              </a>
            ) : null}
          </div>

          {error ? (
            <p className="status-message" style={{ color: '#dc2626' }}>
              {error}
            </p>
          ) : null}
          {image || error ? null : (
            <p className="tool-live-panel-note">
              Pick an image to convert. Transparent areas are filled with the background color.
            </p>
          )}
        </section>

        <section className="tool-live-preview">
          <canvas
            ref={canvasRef}
            style={{
              maxWidth: '100%',
              height: 'auto',
              borderRadius: 12,
              display: image ? 'block' : 'none',
            }}
          />
          <p className="tool-live-panel-note">
            Conversion runs locally with an HTML canvas. Your image never leaves your device.
          </p>
        </section>
      </div>
    </div>
  );
}
